import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEllipsisV, faEye, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ---------------- Search Component ---------------- */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    className="form-control"
    placeholder="Search..."
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
  />
);

/* ---------------- Table Component ---------------- */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  setGlobalFilter,
  pageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter: tableSetFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize, globalFilter },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  React.useEffect(() => {
    tableSetFilter(globalFilter);
  }, [globalFilter]);

  const { pageIndex } = state;

  return (
    <>
      <table className="table table-striped" {...getTableProps()}>
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}{" "}
                  {col.isSorted ? (col.isSortedDesc ? "🔽" : "🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn btn-secondary"
          disabled={!canPreviousPage}
          onClick={previousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          disabled={!canNextPage}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* ---------------- Main Component ---------------- */
const RequirementAssessmentListing = ({accessOfDepartments, permissions}) => {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);

  /* ------------ Fetch Data ------------ */
  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(
        `${SERVER_BASE}/requirement-assessments`
      );
      setTableData(data);
    } catch (error) {
      console.error("Error loading assessments:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ------------ Columns ------------ */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },

      {
        Header: "Date",
        accessor: "createdAt",
        Cell: ({ value }) => moment(value).format("DD-MMM-YY"),
      },
      {
        Header: "Assessment Code",
        accessor: "assessmentCode",
      },

      {
        Header: "Project",
        accessor: "projectName",
      },

      {
        Header: "Requirement Codes",
        accessor: "requirementCodes",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "#",
        Cell: ({ row }) => {
          const d = row.original;

          return (
            <div className="dropdown">
              <button className="btn btn-light" data-bs-toggle="dropdown">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {/* Preview */}
                {permissions?.edit?
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      navigate("../../RequirementAssessment", {
                        state: { id: d.id },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                </li>
                :null}

                <li>
                  <hr className="dropdown-divider" />
                </li>

                <li className="dropdown-item text-muted">
                  <FontAwesomeIcon icon={faPlus} /> {d.createdByName}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [navigate, permissions]
  );

  return (
    <div className="formcontainer mt-5">
    <div className="d-flex align-items-center justify-content-between mb-3">
        <h4>Assessment</h4>
        <div className="d-flex gap-3">

          <button
            className="btn btn-outline-primary"
            onClick={() =>
              navigate("../../RequirementReport", {
                state: { accessOfDepartments }
              })
            }
          >
            Get Report
          </button>
        </div>
      </div>

      {/* ---------------- TOP BAR ---------------- */}
      <div className="row align-items-center mb-3">
      <div className="col-md-2 mb-2">
          {permissions?.write && (
            <button
              className="btn btn-outline-success w-100"
              onClick={() =>
                navigate("../../RequirementAssessment", {
                  state: { accessOfDepartments }
                })
              }
            >
              Add
            </button>
          )}
        </div>
        {/* Search */}
        <div className="col-md-8 mb-2">
          <GlobalFilter
            filter={globalFilter}
            setFilter={setGlobalFilter}
          />
        </div>

        {/* Page Size */}
        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[20, 30, 50, 100].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ---------------- TABLE ---------------- */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={tableData}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

export default RequirementAssessmentListing;
